import OpenAI from 'openai';
import { Task } from '../types';
import { addDays, isPast, isToday, parseISO } from 'date-fns';

// Dynamic model selection based on availability
const AI_MODELS = {
  DEFAULT: 'gpt-3.5-turbo',
  ADVANCED: 'gpt-4',
  LATEST: 'gpt-4-turbo-preview'
};

class AIModelSelector {
  static async getBestAvailableModel(openai: OpenAI): Promise<string> {
    try {
      const models = await openai.models.list();
      const availableModels = models.data.map(model => model.id);
      
      if (availableModels.includes(AI_MODELS.LATEST)) return AI_MODELS.LATEST;
      if (availableModels.includes(AI_MODELS.ADVANCED)) return AI_MODELS.ADVANCED;
      return AI_MODELS.DEFAULT;
    } catch (error) {
      console.warn('Failed to fetch AI models, using default:', error);
      return AI_MODELS.DEFAULT;
    }
  }
}

export class AITaskManager {
  private static openai: OpenAI;
  private static currentModel: string = AI_MODELS.DEFAULT;

  private static async initialize() {
    if (!this.openai) {
      this.openai = new OpenAI({
        apiKey: import.meta.env.VITE_OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
      });
      this.currentModel = await AIModelSelector.getBestAvailableModel(this.openai);
    }
  }

  static async analyzeTasks(tasks: Task[]): Promise<Task[]> {
    await this.initialize();
    
    const today = new Date();
    const activeTasks = tasks.filter(task => task.status !== 'completed');
    const overdueTasks = activeTasks.filter(task => 
      isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))
    );

    if (overdueTasks.length > 0) {
      const updatedTasks = await this.reprioritizeOverdueTasks(overdueTasks);
      return tasks.map(task => 
        updatedTasks.find(t => t.id === task.id) || task
      );
    }

    return tasks;
  }

  static async analyzeNewTask(title: string, description: string): Promise<TaskAnalysis> {
    await this.initialize();

    const systemPrompt = `You are an advanced task management AI that excels at:
    1. Understanding task complexity and urgency
    2. Identifying implicit deadlines
    3. Breaking down tasks into manageable steps
    4. Adapting to changing priorities
    5. Considering user workload and context`;

    const userPrompt = `Analyze this task and provide a detailed assessment:
    Title: ${title}
    Description: ${description}
    
    Consider:
    - Task complexity and scope
    - Required effort and resources
    - Dependencies and prerequisites
    - Potential risks and bottlenecks
    - Impact on other tasks
    
    Respond in JSON format:
    {
      "priority": "urgent|normal|leisure",
      "suggestedDueDate": "YYYY-MM-DD",
      "reasoning": "detailed explanation",
      "suggestedSteps": [
        {"description": "step 1"},
        {"description": "step 2"}
      ]
    }`;

    try {
      const response = await this.openai.chat.completions.create({
        model: this.currentModel,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt }
        ],
        temperature: 0.3,
      });

      const analysis = JSON.parse(response.choices[0].message.content || "{}");
      return {
        priority: analysis.priority || 'normal',
        suggestedDueDate: analysis.suggestedDueDate || addDays(new Date(), 7).toISOString().split('T')[0],
        reasoning: analysis.reasoning || 'Default analysis',
        suggestedSteps: analysis.suggestedSteps || []
      };
    } catch (error) {
      console.error('AI Analysis failed:', error);
      return {
        priority: 'normal',
        suggestedDueDate: addDays(new Date(), 7).toISOString().split('T')[0],
        reasoning: 'AI analysis failed, using default settings',
        suggestedSteps: []
      };
    }
  }

  private static async reprioritizeOverdueTasks(tasks: Task[]): Promise<Task[]> {
    await this.initialize();

    const systemPrompt = `You are an AI task manager specializing in:
    1. Task reprioritization
    2. Deadline adjustment
    3. Workload balancing
    4. Risk assessment
    5. Resource optimization`;

    const userPrompt = `Analyze these overdue tasks and suggest optimized priorities and deadlines:
    ${JSON.stringify(tasks, null, 2)}
    
    Consider:
    1. Original priority and deadline
    2. Current progress and remaining work
    3. Dependencies between tasks
    4. Available resources and constraints
    5. Impact on project timeline
    
    Respond with updated tasks in JSON format, maintaining the same structure but with updated priority, dueDate, and reasoning fields.`;

    try {
      const response = await this.openai.chat.completions.create({
        model: this.currentModel,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt }
        ],
        temperature: 0.3,
      });

      const updatedTasks = JSON.parse(response.choices[0].message.content || "[]");
      return updatedTasks.map((task: Task) => ({
        ...task,
        priority: task.priority || 'urgent',
        dueDate: task.dueDate || addDays(new Date(), 1).toISOString().split('T')[0]
      }));
    } catch (error) {
      console.error('Task reprioritization failed:', error);
      return tasks.map(task => ({
        ...task,
        priority: 'urgent',
        dueDate: addDays(new Date(), 1).toISOString().split('T')[0]
      }));
    }
  }
}

export interface TaskAnalysis {
  priority: 'urgent' | 'normal' | 'leisure';
  suggestedDueDate: string;
  reasoning: string;
  suggestedSteps: Array<{ description: string }>;
}